<script setup lang="ts">
import {ref} from 'vue';
import {useSentry} from '@/ts/composables/pure/use-sentry';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {SENTRY, GIT_HASH} from '@/ts/utils/consts';

const sentry = useSentry();
const sentryStatus = ref('');
const sentryLoading = ref(false);

function testSentryError(): void {
  const timestamp = Date.now();
  sentryLoading.value = true;
  sentryStatus.value = '';
  try {
    sentry.captureException(new Error(`[Debug] Test error #${timestamp}`));
    sentryStatus.value = `Error sent (#${timestamp}). Check Sentry dashboard.`;
  } catch (e) {
    sentryStatus.value = `Failed: ${(e as Error).message}`;
  } finally {
    sentryLoading.value = false;
  }
}

function testSentryMessage(): void {
  const timestamp = Date.now();
  sentryLoading.value = true;
  sentryStatus.value = '';
  try {
    sentry.captureMessage(`[Debug] Test message #${timestamp}`, 'info');
    sentryStatus.value = `Message sent (#${timestamp}). Check Sentry dashboard.`;
  } catch (e) {
    sentryStatus.value = `Failed: ${(e as Error).message}`;
  } finally {
    sentryLoading.value = false;
  }
}
</script>

<template>
  <div class="account-tab-page">
    <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
    <h3 class="info-title">
      Debug
    </h3>

    <section class="debug-section">
      <h4 class="section-title">
        Sentry Integration
      </h4>

      <div class="info-card">
        <div class="info-row">
          <span class="info-label">Environment:</span>
          <span class="info-value">{{ SENTRY?.ENVIRONMENT ?? 'N/A' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Release:</span>
          <span class="info-value">{{ GIT_HASH }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Sample Rate:</span>
          <span class="info-value">{{ SENTRY?.TRACES_SAMPLE_RATE ?? 'N/A' }}</span>
        </div>
      </div>

      <div class="actions">
        <balm-btn
          label="Send Test Error"
          theme="red"
          paddings="p2-5"
          :loading="sentryLoading"
          @click="testSentryError"
        />
        <balm-btn
          label="Send Test Message"
          theme="green"
          paddings="p2-5"
          :loading="sentryLoading"
          @click="testSentryMessage"
        />
      </div>

      <div v-if="sentryStatus" class="status-banner" :class="sentryStatus.startsWith('Failed') ? 'error' : 'success'">
        {{ sentryStatus }}
      </div>
    </section>
    <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
  </div>
</template>

<style scoped lang="scss">
.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);
}

.info-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-5);
}

.debug-section {
  margin-top: var(--Spacing-spacing-5);

  .section-title {
    @include font-h3;
    margin-bottom: var(--Spacing-spacing-3);
  }
}

.info-card {
  @include q-card-10;
  padding: var(--Spacing-spacing-4);
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-2);
}

.info-row {
  display: flex;
  gap: var(--Spacing-spacing-2);

  .info-label {
    @include font-body-bold;
    min-width: 120px;
  }

  .info-value {
    @include font-body;
    word-break: break-all;
  }
}

.actions {
  display: flex;
  gap: var(--Spacing-spacing-3);
  margin-top: var(--Spacing-spacing-4);
}

.status-banner {
  margin-top: var(--Spacing-spacing-3);
  padding: var(--Spacing-spacing-3);
  border-radius: 4px;
  @include font-body;

  &.success {
    background-color: var(--Success-success-10, #e6f9e6);
    color: var(--Success-success-100, #1a7a1a);
  }

  &.error {
    background-color: var(--Error-error-10, #fce6e6);
    color: var(--Error-error-100, #c0392b);
  }
}
</style>
